// The single definition of "onboarding is complete".
//
// House rule: payment never gates onboarding. The $19 monthly fee exists only on
// the suite path, and members who bring their own California address never pay it.
// Onboarding is complete when every gate the member's own path requires has passed.
//
// This file is the one definition. The client imports it directly
// (src/lib/onboardingComplete.ts re-exports it) and edge functions import it here.

export type OnboardingRecord = {
  legal_address?: unknown;
  una_name?: string | null;
  una_name_status?: string | null;
  una_name_confirmed_at?: string | null;
  mea_status?: string | null;
  mea_signed_at?: string | null;
  ca_address_option?: string | null;
  ca_address?: unknown;
  suite_number?: number | null;
  lease_status?: string | null;
  lease_signed_at?: string | null;
  payment_status?: string | null;
} | null | undefined;

function hasAddress(value: unknown): boolean {
  if (!value || typeof value !== "object") return false;
  const a = value as Record<string, unknown>;
  return Boolean(a.line1 || a.city || a.zip);
}

/** Their own details are in. */
export function intakeDone(record: OnboardingRecord): boolean {
  if (!record) return false;
  return hasAddress(record.legal_address) || Boolean(record.una_name);
}

/** The name is approved and the member has confirmed their record. */
export function nameSettled(record: OnboardingRecord): boolean {
  if (!record) return false;
  return record.una_name_status === "approved" && Boolean(record.una_name_confirmed_at);
}

/** The Member Engagement Agreement is signed. */
export function meaSigned(record: OnboardingRecord): boolean {
  if (!record) return false;
  return record.mea_status === "signed" || Boolean(record.mea_signed_at);
}

/** True only for members who took the Wealthy Steward suite. */
export function isSuitePath(record: OnboardingRecord): boolean {
  if (!record) return false;
  return record.ca_address_option === "platform" || record.suite_number != null;
}

/**
 * The address is settled either way: their own California address is saved,
 * or a suite is assigned and the lease is signed.
 */
export function addressSettled(record: OnboardingRecord): boolean {
  if (!record) return false;
  if (record.ca_address_option === "own" && hasAddress(record.ca_address)) return true;
  const leaseSigned = record.lease_status === "signed" || Boolean(record.lease_signed_at);
  return record.suite_number != null && leaseSigned;
}

/** Payment is informational only. It is never part of this answer. */
export function isOnboardingComplete(record: OnboardingRecord): boolean {
  if (!record) return false;
  return (
    intakeDone(record) && nameSettled(record) && meaSigned(record) && addressSettled(record)
  );
}
